import { useCallback } from 'react';
import { ASSIGNMENT_EDITOR_ACTION, ASSIGNMENT_VALIDATION_RESULT } from 'utils/assignmentEditorConstants';
import { ASSIGNMENT_PERSISTENCE_INTENT } from 'utils/assignmentPersistencePolicy';

const useAssignmentEditorCommands = ({
    autosave,
    closeDialog,
    deleteAssignment,
    form,
    history,
    isNewRoute,
    message,
    persistedAssignment,
    persistedAssignmentRef,
    persist,
    reconcileLms,
    setDialog,
    setIsSaving,
    t,
    validateForPublish,
}) => {
    const persistCommand = useCallback(
        async (intent, apiData, rubric) => {
            setIsSaving(true);
            try {
                const result = await persist({ snapshot: form, intent, apiData, rubric });
                if (result.cleanupErrors.length) {
                    message.error(
                        t(
                            'message.cleanup-error',
                            'Your assignment was saved, but related preview data could not be fully updated.'
                        )
                    );
                }
                return result;
            } catch (error) {
                message.error(t('message.save-error', 'We could not save your assignment. Please try again.'));
                return null;
            } finally {
                setIsSaving(false);
            }
        },
        [form, message, persist, setIsSaving, t]
    );

    const publish = useCallback(
        async ({ revalidate = true } = {}) => {
            if (revalidate) {
                const validation = validateForPublish();
                if (!validation.isValid)
                    return { status: ASSIGNMENT_VALIDATION_RESULT.INVALID, errors: validation.errors };
            }
            if (!(await autosave.save())) {
                message.error(t('message.save-error', 'We could not save your assignment. Please try again.'));
                return { status: ASSIGNMENT_VALIDATION_RESULT.FAILED };
            }
            const commit = async ({ apiData, rubric } = {}) => {
                const result = await persistCommand(ASSIGNMENT_PERSISTENCE_INTENT.PUBLISH, apiData, rubric);
                if (!result) return { status: ASSIGNMENT_VALIDATION_RESULT.FAILED };
                message.success(
                    t(
                        isNewRoute ? 'message.create-success' : 'message.update-success',
                        isNewRoute
                            ? 'Your assignment was successfully created.'
                            : 'Your assignment was successfully updated.'
                    )
                );
                history.push(`/assignments/${result.assignment.id}`);
                return { status: ASSIGNMENT_VALIDATION_RESULT.SUCCESS, assignment: result.assignment };
            };
            return reconcileLms({
                intent: ASSIGNMENT_PERSISTENCE_INTENT.PUBLISH,
                onContinue: commit,
                onCancel: autosave.resume,
            });
        },
        [autosave, history, isNewRoute, message, persistCommand, reconcileLms, t, validateForPublish]
    );

    const unpublish = useCallback(async () => {
        if (isNewRoute) return { status: ASSIGNMENT_VALIDATION_RESULT.FAILED };
        autosave.pause();
        if ((await autosave.waitForIdle()) === false) {
            autosave.resume();
            return { status: ASSIGNMENT_VALIDATION_RESULT.FAILED };
        }
        const commit = async ({ apiData, rubric } = {}) => {
            const result = await persistCommand(ASSIGNMENT_PERSISTENCE_INTENT.UNPUBLISH, apiData, rubric);
            if (!result) {
                autosave.resume();
                return { status: ASSIGNMENT_VALIDATION_RESULT.FAILED };
            }
            message.success(t('message.update-success', 'Your assignment was successfully updated.'));
            history.push(`/assignments/${result.assignment.id}`);
            return { status: ASSIGNMENT_VALIDATION_RESULT.SUCCESS, assignment: result.assignment };
        };
        return reconcileLms({
            intent: ASSIGNMENT_PERSISTENCE_INTENT.UNPUBLISH,
            onContinue: commit,
            onCancel: autosave.resume,
        });
    }, [autosave, history, isNewRoute, message, persistCommand, reconcileLms, t]);

    const onClickSave = useCallback(
        async ({ key }) => {
            if (key === ASSIGNMENT_EDITOR_ACTION.UNPUBLISH) return unpublish();
            if (key === ASSIGNMENT_EDITOR_ACTION.PUBLISH) return publish();
            const currentPersistedAssignment = persistedAssignmentRef.current || persistedAssignment;
            if (currentPersistedAssignment?.id && !currentPersistedAssignment.is_draft) {
                const validation = validateForPublish();
                if (!validation.isValid)
                    return { status: ASSIGNMENT_VALIDATION_RESULT.INVALID, errors: validation.errors };
            }
            if (!persistedAssignmentRef.current?.id) autosave.markDirty();
            const saved = await autosave.save();
            return {
                status: saved ? ASSIGNMENT_VALIDATION_RESULT.SUCCESS : ASSIGNMENT_VALIDATION_RESULT.FAILED,
            };
        },
        [autosave, persistedAssignment, persistedAssignmentRef, publish, unpublish, validateForPublish]
    );

    const onClickPublish = useCallback(() => {
        const validation = validateForPublish();
        if (!validation.isValid) return { status: ASSIGNMENT_VALIDATION_RESULT.INVALID, errors: validation.errors };
        if (!form.aiStudentFeedbackEnabled) return publish({ revalidate: false });
        return setDialog({
            open: true,
            message: t('dialog.ready-to-publish.title', 'Ready to Publish?'),
            description: t(
                'dialog.ready-to-publish.description',
                'Students will be notified that they can upload their assignments to get instant feedback. Not ready yet? You can save this assignment as a draft.'
            ),
            variant: 'primary',
            actionButtonText: t('common:controls.publish', 'Publish'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                closeDialog();
                publish();
            },
            onCancel: closeDialog,
            onClose: closeDialog,
        });
    }, [closeDialog, form.aiStudentFeedbackEnabled, publish, setDialog, t, validateForPublish]);

    const onClickDelete = useCallback(() => {
        const id = persistedAssignmentRef.current?.id || persistedAssignment?.id;
        if (!id) return;
        autosave.pause();
        setDialog({
            open: true,
            message: t('dialog.delete-assignment.title', 'Delete Assignment?'),
            description: t('dialog.delete-assignment.description', 'This action cannot be undone.'),
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                try {
                    await autosave.waitForIdle();
                    await deleteAssignment(id);
                    closeDialog();
                    history.push('/assignments');
                } catch (error) {
                    autosave.resume();
                    message.error(t('message.delete-error', 'We could not delete your assignment. Please try again.'));
                }
            },
            onCancel: () => {
                autosave.resume();
                closeDialog();
            },
            onClose: () => {
                autosave.resume();
                closeDialog();
            },
        });
    }, [
        autosave,
        closeDialog,
        deleteAssignment,
        history,
        message,
        persistedAssignment,
        persistedAssignmentRef,
        setDialog,
        t,
    ]);

    const onClickCancel = useCallback(() => {
        autosave.pause();
        history.push(persistedAssignment?.id ? `/assignments/${persistedAssignment.id}` : '/assignments');
    }, [autosave, history, persistedAssignment]);

    return { onClickCancel, onClickDelete, onClickPublish, onClickSave };
};

export default useAssignmentEditorCommands;
